import { OrganizationContext } from '../../../components/contexts/organization';
import { SearchFormValues } from '../detail/UsageStatusDetail';
import {
  CouponPackLogDetailJsonConnection,
  useInvokeCouponPackLogDetailCsvUrlLazyQuery,
  useInvokeCouponPackLogDetailJsonLazyQuery,
  usePollCouponPackLogDetailCsvUrlLazyQuery,
  usePollCouponPackLogDetailJsonLazyQuery,
} from '../../../graphql/generated';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Bugsnag from '@bugsnag/js';
import { downloadCsvFileWithBom } from '../../../util/download';
import { formatJstDate } from '../../../util';

const Limit = 25;

export type CouponPackUsageStatusDetail = {
  organization?: OrganizationContext;
  loading: boolean;
  onSearch: (condition: SearchFormValues) => void;
  onCsvOut: (condition: SearchFormValues) => void;
  csvLoading: boolean;
  data?: CouponPackLogDetailJsonConnection;
  onFetchMore: (condition: SearchFormValues) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  allAcquired: boolean;
};

type UseCouponPackUsageStatusDetailProps = {
  couponPackMasterId: string;
  couponPackCode: string;
  couponPackName: string;
  couponCode: string;
  couponName: string;
};

export const useCouponPackUsageStatusDetail = ({
  couponPackMasterId,
  couponPackCode,
  couponPackName,
  couponCode,
  couponName,
}: UseCouponPackUsageStatusDetailProps): CouponPackUsageStatusDetail => {
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext.organization;
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const [csvLoading, setCsvLoading] = useState(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [allAcquired, setAllAcquired] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const getDefaultQueryValues = () => {
    return {
      couponPackCode,
      couponPackName,
      couponCode,
      couponName,
    };
  };

  // 利用状況集計 ポーリング
  const [pollJsonQuery, statusContext] = usePollCouponPackLogDetailJsonLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first', // これをつけないと fetchMore 時に nextTokenなしで取得しにいってしまう
    pollInterval: 5000,
  });

  // 利用状況集計 取得
  // ApolloClient で ポーリングとfetchMoreを同時に利用できないため分ける
  const [getJsonQuery, getJsonQueryContext] = usePollCouponPackLogDetailJsonLazyQuery({
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      if (Number(data?.pollCouponPackLogDetailJson?.nodes?.length) < Limit) {
        setAllAcquired(true);
      }
      setLoading(false);
    },
  });

  // 利用状況集計取得 invoke
  const [invokeJsonQuery] = useInvokeCouponPackLogDetailJsonLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      pollJsonQuery({
        variables: {
          queryExecutionId: data.invokeCouponPackLogDetailJson.queryExecutionId,
        },
      });
      setOffset(offset + Limit);
    },
    onError: (e) => {
      setLoading(false);
      Bugsnag.notify(e);
      enqueueSnackbar('利用状況集計一覧の取得に失敗しました', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });

  // 利用状況一覧 CSV URL ポーリング
  const [pollCsvUrlQuery, statusCsvUrlContext] = usePollCouponPackLogDetailCsvUrlLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    pollInterval: 5000,
  });

  // 利用状況CSV取得 invoke
  const [invokeCsvQuery] = useInvokeCouponPackLogDetailCsvUrlLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      pollCsvUrlQuery({
        variables: {
          queryExecutionId: data.invokeCouponPackLogDetailCsvUrl.queryExecutionId,
        },
      });
    },
    onError: (e) => {
      setCsvLoading(false);
      Bugsnag.notify(e);
      enqueueSnackbar('利用状況集計一覧の取得に失敗しました', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });

  // 検索実行
  const onSearchJsonSubmit = useCallback(
    (data: SearchFormValues) => {
      if (!organization) {
        return;
      }
      setOffset(0);
      setLoading(true);
      setAllAcquired(false);
      invokeJsonQuery({
        variables: {
          query: {
            adminOrganizationId: organization.id,
            couponPackMasterId: couponPackMasterId,
            startDate: data.startDate ? formatJstDate(new Date(data.startDate).toISOString()) : undefined,
            endDate: data.endDate ? formatJstDate(new Date(data.endDate).toISOString()) : undefined,
            limit: Limit,
            offset: offset,
            couponStatus: data.couponStatus || undefined,
            usageShopName: data.usageShopName || undefined,
            ...getDefaultQueryValues(),
          },
        },
      });
    },
    [organization, invokeJsonQuery]
  );

  // CSV出力
  const onCsvOutSubmit = useCallback(
    (data: SearchFormValues) => {
      if (!organization) {
        return;
      }
      setCsvLoading(true);
      invokeCsvQuery({
        variables: {
          query: {
            adminOrganizationId: organization.id,
            couponPackMasterId: couponPackMasterId,
            startDate: data.startDate ? formatJstDate(new Date(data.startDate).toISOString()) : undefined,
            endDate: data.endDate ? formatJstDate(new Date(data.endDate).toISOString()) : undefined,
            limit: Limit,
            offset: offset,
            couponStatus: data.couponStatus || undefined,
            usageShopName: data.usageShopName || undefined,
            ...getDefaultQueryValues(),
          },
        },
      });
    },
    [organization, invokeCsvQuery]
  );

  // JSONクエリ出力をポーリングする
  // ポーリングクエリの onComplete は呼ばれないので useEffect でやるしかない
  useEffect(() => {
    const status = statusContext?.data?.pollCouponPackLogDetailJson?.status;
    const hasError = statusContext?.error;

    // 終了系ステータスの場合、ポーリングを止める
    if (status === 'CANCELLED' || status === 'FAILED' || status === 'SUCCEEDED' || hasError) {
      if (statusContext && statusContext.stopPolling) {
        statusContext.stopPolling();
      }
    }

    // 正常に終了した場合は、データフェッチ用のクエリを呼び出してステータスを終了にする
    if (
      status === 'SUCCEEDED' &&
      statusContext &&
      statusContext.data &&
      statusContext.data.pollCouponPackLogDetailJson &&
      statusContext.data.pollCouponPackLogDetailJson.nodes
    ) {
      if (
        fetching &&
        organization &&
        getJsonQueryContext &&
        getJsonQueryContext.fetchMore &&
        getJsonQueryContext.variables
      ) {
        setLoading(true);
        getJsonQueryContext.fetchMore({
          variables: statusContext.variables,
          updateQuery: (prevQuery, { fetchMoreResult }) => {
            setLoading(false);
            if (!fetchMoreResult) {
              return prevQuery;
            }
            // 取得件数が0件の場合は、全件取得済みとみなす
            if (Number(fetchMoreResult?.pollCouponPackLogDetailJson?.nodes?.length) < Limit) {
              setAllAcquired(true);
              enqueueSnackbar('全件取得済みです', { variant: 'success' });
            } else {
              enqueueSnackbar('集計処理が正常に終了しました', { variant: 'success' });
            }
            return {
              pollCouponPackLogDetailJson: {
                __typename: fetchMoreResult.pollCouponPackLogDetailJson.__typename,
                queryExecutionId: fetchMoreResult.pollCouponPackLogDetailJson.queryExecutionId,
                status: fetchMoreResult.pollCouponPackLogDetailJson.status,
                nodes: [
                  ...(prevQuery.pollCouponPackLogDetailJson.nodes || []),
                  ...(fetchMoreResult.pollCouponPackLogDetailJson.nodes || []),
                ],
                pageInfo: fetchMoreResult.pollCouponPackLogDetailJson.pageInfo,
                total: fetchMoreResult.pollCouponPackLogDetailJson.total,
              },
            };
          },
        });
        setFetching(false);
      } else {
        getJsonQuery({
          variables: statusContext.variables,
        });
        enqueueSnackbar('集計処理が正常に終了しました', { variant: 'success' });
      }
    } else if (status === 'FAILED' || status === 'CANCELLED' || hasError) {
      setLoading(false);
      enqueueSnackbar('集計処理でエラーが発生しました', {
        variant: 'error',
        autoHideDuration: 5000,
      });
      Bugsnag.notify(statusContext.error || new Error('polling error'));
    }
  }, [statusContext.data?.pollCouponPackLogDetailJson.status, statusContext?.error]);

  // CSV URL クエリ出力をポーリングする
  // ポーリングクエリの onComplete は呼ばれないので useEffect でやるしかない
  useEffect(() => {
    const status = statusCsvUrlContext?.data?.pollCouponPackLogDetailCsvUrl?.status;
    const hasError = statusCsvUrlContext?.error;

    // 終了系ステータスの場合、ポーリングを止める
    if (status === 'CANCELLED' || status === 'FAILED' || status === 'SUCCEEDED' || hasError) {
      if (statusCsvUrlContext && statusCsvUrlContext.stopPolling) {
        statusCsvUrlContext.stopPolling();
      }
    }

    // 正常に終了した場合は、CSVファイルをダウンロード
    if (
      status === 'SUCCEEDED' &&
      statusCsvUrlContext &&
      statusCsvUrlContext.data &&
      statusCsvUrlContext.data.pollCouponPackLogDetailCsvUrl
    ) {
      enqueueSnackbar('集計処理が正常に終了しました。CSVファイルをダウンロードします。', { variant: 'success' });
      setCsvLoading(false);
      if (statusCsvUrlContext.data.pollCouponPackLogDetailCsvUrl.preSignedUrl)
        downloadCsvFileWithBom(statusCsvUrlContext.data.pollCouponPackLogDetailCsvUrl.preSignedUrl);
    } else if (status === 'FAILED' || status === 'CANCELLED' || hasError) {
      setCsvLoading(false);
      enqueueSnackbar('集計処理でエラーが発生しました', {
        variant: 'error',
        autoHideDuration: 5000,
      });
      Bugsnag.notify(statusCsvUrlContext.error || new Error('polling error'));
    }
  }, [
    statusCsvUrlContext.data?.pollCouponPackLogDetailCsvUrl.status,
    statusCsvUrlContext.data?.pollCouponPackLogDetailCsvUrl.preSignedUrl,
    statusCsvUrlContext?.error,
  ]);

  // 更に読み込む
  const onFetchMoreJson = useCallback(
    async (data: SearchFormValues) => {
      if (statusContext && statusContext.stopPolling) {
        statusContext?.stopPolling();
      }
      if (organization && statusContext && statusContext.fetchMore && statusContext.variables) {
        setLoading(true);
        setFetching(true);
        await invokeJsonQuery({
          variables: {
            query: {
              adminOrganizationId: organization.id,
              couponPackMasterId: couponPackMasterId,
              startDate: data.startDate ? formatJstDate(new Date(data.startDate).toISOString()) : undefined,
              endDate: data.endDate ? formatJstDate(new Date(data.endDate).toISOString()) : undefined,
              limit: Limit,
              offset: offset,
              couponStatus: data.couponStatus || undefined,
              usageShopName: data.usageShopName || undefined,
              ...getDefaultQueryValues(),
            },
          },
        });
      }
    },
    [statusContext, getJsonQueryContext, organization]
  );

  return {
    organization,
    loading,
    onSearch: onSearchJsonSubmit,
    onCsvOut: onCsvOutSubmit,
    csvLoading,
    data: getJsonQueryContext.data?.pollCouponPackLogDetailJson,
    onFetchMore: onFetchMoreJson,
    open,
    setOpen,
    allAcquired,
  };
};
